import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Loader2, AlertCircle } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import {
  formatCalendarOption,
  formatHolidayDate,
  formatHolidayOption,
} from '../utils/laborCalendarUtils';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';

export function LaborDeleteHolidayModal({
  isOpen,
  onClose,
  factoryId,
  factoryCode,
  defaultYear,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const [calendarId, setCalendarId] = useState('');
  const [holidayId, setHolidayId] = useState('');
  const [error, setError] = useState(null);

  const { data: calendars = [], isLoading: loadingCalendars } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled: isOpen && Boolean(factoryId),
  });

  const {
    data: holidaysRaw = [],
    isLoading: loadingHolidays,
    isFetching: fetchingHolidays,
  } = useQuery({
    queryKey: ['labor-holidays', calendarId],
    queryFn: () => laborScheduleService.listHolidaysByCalendar(calendarId),
    enabled: isOpen && Boolean(calendarId),
  });

  const holidays = useMemo(() => {
    const yearPrefix = String(defaultYear);
    return [...holidaysRaw]
      .filter((h) => formatHolidayDate(h.date).startsWith(yearPrefix))
      .sort((a, b) => formatHolidayDate(a.date).localeCompare(formatHolidayDate(b.date)));
  }, [holidaysRaw, defaultYear]);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setHolidayId('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || calendars.length === 0) return;
    const match = calendars.find((c) => Number(c.year) === Number(defaultYear));
    setCalendarId(String((match || calendars[0]).id));
  }, [isOpen, calendars, defaultYear]);

  useEffect(() => {
    setHolidayId('');
  }, [calendarId]);

  useEffect(() => {
    if (holidays.length > 0 && !holidayId) {
      setHolidayId(String(holidays[0].id));
    }
  }, [holidays, holidayId]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!holidayId) throw new Error('Selecciona un festivo.');
      return laborScheduleService.deleteHoliday(holidayId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['labor-holidays', calendarId] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-pattern-list'] });
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudo eliminar el festivo');
    },
  });

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={mutation.isPending}
        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mutation.mutate();
        }}
        disabled={mutation.isPending || !holidayId || loadingHolidays}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Eliminar festivo
      </button>
    </>
  );

  return (
    <LaborCalendarModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Eliminar festivo"
      icon={Trash2}
      footer={footer}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <p className="text-sm text-gray-400 mb-4">
        Fábrica <span className="text-white font-medium">{factoryCode}</span>. El día del
        calendario volverá a ser laborable.
      </p>

      <label htmlFor="labor-del-hol-cal" className="block text-sm text-gray-300 mb-2">
        Calendario
      </label>
      {loadingCalendars ? (
        <p className="text-gray-500 text-sm mb-4">Cargando calendarios…</p>
      ) : (
        <select
          id="labor-del-hol-cal"
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          disabled={mutation.isPending}
          className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm mb-4"
        >
          {calendars.map((cal) => (
            <option key={cal.id} value={cal.id}>
              {formatCalendarOption(cal)}
            </option>
          ))}
        </select>
      )}

      <label htmlFor="labor-del-hol" className="block text-sm text-gray-300 mb-2">
        Festivo <span className="text-red-400">*</span>
      </label>
      {loadingHolidays || fetchingHolidays ? (
        <p className="text-gray-500 text-sm mb-4 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando festivos…
        </p>
      ) : holidays.length === 0 ? (
        <p className="text-amber-400 text-sm mb-4 bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
          No hay festivos registrados en este calendario.
        </p>
      ) : (
        <select
          id="labor-del-hol"
          value={holidayId}
          onChange={(e) => setHolidayId(e.target.value)}
          disabled={mutation.isPending}
          className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
        >
          {holidays.map((h) => (
            <option key={h.id} value={h.id}>
              {formatHolidayOption(h)}
            </option>
          ))}
        </select>
      )}
    </LaborCalendarModalShell>
  );
}
